import { PackageProviderName, SettingKey, WizardStepName } from 'data/ppoints/constants';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import { usePackageProviders } from 'hooks/usePackageProviders';
import {
    ShippingMethodsSetting,
    ShippingMethodsSettingProviders,
} from 'models/ppoints/settings/ShippingMethodsSetting';
import { ZonesAndProvidersSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { settingsSetThunk } from 'redux/slices/settings-slice';
import { ShippingZoneRateStepForm } from '../models/shipping-zone-rate-step-form';
import useWizard from './useWizard';
import { MappedZone } from './useZonesWithMappedProviders';

export const getZoneFieldKey = (zoneId: number) => `zone_${zoneId}`;

const getProviderKey = (provider: PackageProviderName) => `provider_${provider}`;

type ActivatedProviderIdsByZone = Record<string, number[]>;

const useSaveZoneRates = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const wizard = useWizard();
    const settings = useAppSelector((state) => state.settings.settings);
    const { packageProviders } = usePackageProviders();
    const { setBanner, emptyBanner } = useErrorBanner();

    const setSaveErrorBanner = useCallback(() => {
        setBanner({
            titleKey: t('SHIPPING_RATES.ERROR.SAVING_RATES'),
            descriptionKey: t('SHIPPING_RATES.ERROR.SAVING_RATES_SUBTEXT'),
            tone: 'warning',
            withSupportActions: true,
        });
    }, [setBanner, t]);

    const saveZoneRates = useCallback(
        async (zones: MappedZone[], data: ShippingZoneRateStepForm): Promise<boolean> => {
            emptyBanner();

            const providerIds = new Map<PackageProviderName, number>(
                (packageProviders ?? []).map((provider) => [provider.name, provider.id])
            );

            if (zones.some((zone) => zone.providers.some((provider) => !providerIds.has(provider)))) {
                setSaveErrorBanner();
                return false;
            }

            const rateMapping: ShippingMethodsSettingProviders = {
                ...((settings?.[SettingKey.shipping_methods] as { providers?: ShippingMethodsSettingProviders })
                    ?.providers ?? {}),
            };

            const activatedProviders: ActivatedProviderIdsByZone = Object.fromEntries(
                Object.entries(
                    (settings?.[SettingKey.zones_and_providers] ?? {}) as unknown as ActivatedProviderIdsByZone
                    // The arrays are cloned: they come straight out of the frozen redux state.
                ).map(([zoneId, ids]) => [zoneId, [...(ids ?? [])]])
            );

            zones.forEach((zone) => {
                const zoneKey = getZoneFieldKey(zone.id);
                const rates = zone.zone.price_based_shipping_rates.concat(zone.zone.weight_based_shipping_rates);
                const selectedByProvider = data.zones?.[zoneKey] ?? {};
                const activatedInZone = new Set(activatedProviders[zone.id] ?? []);

                zone.providers.forEach((provider) => {
                    const selected = selectedByProvider[provider] ?? [];

                    rateMapping[getProviderKey(provider)] = {
                        ...(rateMapping[getProviderKey(provider)] ?? {}),
                        [zoneKey]: rates
                            .filter((rate) => selected.includes(rate.id))
                            .map((rate) => ({ id: rate.id, name: rate.name, price: rate.price })),
                    };

                    if (selected.length) {
                        activatedInZone.add(providerIds.get(provider));
                    } else {
                        activatedInZone.delete(providerIds.get(provider));
                    }
                });

                activatedProviders[zone.id] = [...activatedInZone];
            });

            try {
                await dispatch(
                    settingsSetThunk({
                        settings: {
                            [SettingKey.zones_and_providers]: activatedProviders as unknown as ZonesAndProvidersSetting,
                            [SettingKey.shipping_methods]: { providers: rateMapping } as ShippingMethodsSetting,
                        },
                    })
                ).unwrap();
            } catch {
                setSaveErrorBanner();
                return false;
            }

            shopify.toast.show(t('SHIPPING_RATES.TOASTS.SAVING_RATES'));

            if (!wizard.isAllDone()) {
                await wizard.handleWizardStepper(WizardStepName.shipping_zone_rate);
            }

            return true;
        },
        [dispatch, settings, packageProviders, setSaveErrorBanner, emptyBanner, wizard, t]
    );

    return { saveZoneRates };
};

export default useSaveZoneRates;
